// Shared visual vocabulary for button-like controls. Both <Btn> (a real
// <button>) and <LinkBtn> (a react-router <Link> styled as a button) consume
// these, so a Btn and a LinkBtn of the same size/variant render pixel-identical
// and can never drift apart again.

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning' | 'success';
export type ButtonSize = 'sm' | 'md';

// `no-underline` is inert on a <button> but keeps <LinkBtn> from inheriting the
// default anchor underline.
export const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 font-semibold tracking-wide transition no-underline';

// Every `var(--ui-*, …)` below carries this package's own long-standing value as
// the CSS fallback, so an app that defines none of these tokens renders exactly
// what it rendered before — which is how the driver portal and the ops console
// stay untouched while the admin (apps/admin/src/index.css) opts itself in by
// defining them. Never drop a fallback: an undefined var with no fallback
// collapses the declaration and the control loses its fill entirely.
export const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-[var(--ui-control-radius,0.375rem)]',
  md: 'px-5 py-2.5 text-sm rounded-[var(--ui-control-radius,0.375rem)]',
};

// `disabled:` utilities are inert on an <a>, harmless to share with <LinkBtn>.
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'text-white shadow-sm hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'bg-[var(--ui-btn-secondary-bg,#ffffff)] border border-[var(--ui-btn-secondary-border,#e2e8f0)] text-[var(--ui-btn-secondary-text,#1e293b)] hover:bg-[var(--ui-btn-secondary-hover,#f8fafc)] disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'bg-transparent text-[var(--ui-btn-ghost-text,#334155)] hover:bg-[var(--ui-btn-ghost-hover,#f1f5f9)] disabled:opacity-50 disabled:cursor-not-allowed',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed',
  warning:
    'bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed',
  success:
    'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed',
};

// Primary buttons take their fill from the brand accent token. Callers can still
// override with an explicit `style` prop (e.g. the dashboard CTA), which spreads
// after this and therefore wins.
export const PRIMARY_BG = 'var(--brand-accent)';
